<template>
  <div>
    <b-card
      :title="translations.allRedemptions[lang]"
      @cancel="$emit('cancel')"
      :submitBtn="false"
      :cancel-text="{ el: 'Κλείσιμο', en: 'Close', it: 'Chiudi' }"
    >
    <v-toolbar flat height="90">
      <div class="d-flex flex-column pt-6">
        <v-col>
          <ExportLinks export-key="company-customer-all-coupons" :filters="{ user_id: userAccountId, order: 'date,desc', q: search || '' }" />
        </v-col>
      </div>
      <v-spacer></v-spacer>

      <v-col cols="4">
        <b-search-field v-model="search"></b-search-field>
      </v-col>
    </v-toolbar>
    <b-data-table
      :loading="loading"
      :headers="headers"
      :items="allGifts"
      :pagination="pagination"
      :page.sync="page"
      @page-sync="(p) => (page = p)"
      :no-data-text="translations.noData[lang]"
      item-key="coupon_claim_id"
    >
      <template v-slot:item.coupon_claim_date="{ item }">
        {{ formatDate(item.coupon_claim_date) }}
      </template>
      <template v-slot:item.coupon_claim_expiration_date="{ item }">
        {{ formatDate(item.coupon_claim_expiration_date) }}
      </template>
      <template v-slot:item.decrease_points="{ item }">
        {{ item.decrease_points || '-' }}
      </template>
      <template v-slot:item.coupon_claim_is_expired="{ item }">
        <v-icon v-if="item.coupon_claim_is_expired" color="green">mdi-check-circle-outline</v-icon>
        <v-icon v-else color="red">mdi-close-circle-outline</v-icon>
      </template>
      <template v-if="superUser" v-slot:item.actions="{ item }">
        <v-btn
          color="grey lighten-2"
          class="text-capitalize my-1 red--text"
          depressed
          @click="openCancelDialog(item)"
        >
          {{ translations.cancel[lang] }}
        </v-btn>
      </template>
    </b-data-table>
    </b-card>
    <v-dialog v-model="cancelDialog" max-width="500">
      <b-card
        type="delete"
        :title="translations.cancelRedemption[lang]"
        @cancel="closeCancelDialog"
        @submit="confirmCancelRedemption"
        :submit-text="translations.submit"
        :cancel-text="translations.dialogCancel"
        :loading="cancelLoading"
      >
        <span>{{ translations.cancelRedemptionText[lang] }}</span>
      </b-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from 'moment';
import debounce from 'lodash/debounce';
import { mapState } from 'vuex';
import translations from '@/utils/translations/loyaltyPanel/redemptions';
import Customers from '@/models/loyaltyPanel/Customers';
import ExportLinks from '@/components/general/ExportLinks.vue';

export default {
  name: 'CustomerRedemptionsDialog',

  mixins: [translations],

  components: {
    ExportLinks,
  },

  props: {
    userAccountId: {
      type: [Number, String],
      required: true,
    },
  },

  data() {
    return {
      page: 1,
      search: '',
      loading: false,
      allGifts: [],
      pagination: { current_page: 1, items_per_page: 12, displayed: 0 },
      cancelDialog: false,
      cancelTarget: null,
      cancelLoading: false,
    };
  },

  computed: {
    ...mapState(['superUser']),
    storeMode() {
      return this.$store.state.store?.display_mode || 'points';
    },
    lang() {
      return this.$route.params.lang;
    },
    headers() {
      let headers = [
        {
          text: 'Coupon Claim ID',
          value: 'coupon_claim_id',
        },
        {
          text: this.translations.voucherMobile[this.lang],
          value: 'mobile',
        },
        {
          text: this.translations.giftTitle[this.lang],
          value: 'gift_title',
        },
        {
          text: this.translations.voucherCode[this.lang],
          value: 'voucher',
        },
        {
          text: this.translations[`decrease_${this.storeMode}`][this.lang],
          value: 'decrease_points',
        },
        {
          text: this.translations.couponClaimDate[this.lang],
          value: 'coupon_claim_date',
        },
        {
          text: this.translations.couponIsExpired[this.lang],
          value: 'coupon_claim_is_expired',
        },
        {
          text: this.translations.couponExpirationDate[this.lang],
          value: 'coupon_claim_expiration_date',
        },
        {
          text: this.translations.status[this.lang],
          value: 'status',
        },
        {
          text: 'Redeem ID',
          value: 'redeem_id',
        },
        {
          text: this.translations.redemptionStore[this.lang],
          value: 'redeem_store_name',
        },
      ];
      if (this.superUser) {
        headers.push({
          text: this.translations.actions[this.lang],
          value: 'actions',
        });
      }
      return headers;
    },
  },

  methods: {
    async loadData() {
      this.loading = true;
      try {
        let query = `?page=${this.page || 1}`;
        if (this.search?.length) {
          query += `&q=${this.search}`;
        }
        const { data } = await Customers.getAllGiftsByCustomer(this.userAccountId, query);
        const { coupons, pagination } = data.data;
        this.allGifts = coupons || [];
        this.pagination = pagination;
      } catch (ex) {
        console.error('Failed to fetch customer redemptions', ex);
      } finally {
        this.loading = false;
      }
    },

    formatDate(date) {
      if (!date) return '-';
      return moment(date).format('DD/MM/YYYY HH:mm');
    },

    handleSearch() {
      this.page = 1;
      this.loadData();
    },

    openCancelDialog(item) {
      this.cancelTarget = item;
      this.cancelDialog = true;
    },

    closeCancelDialog() {
      this.cancelDialog = false;
      this.cancelTarget = null;
    },

    async confirmCancelRedemption() {
      if (!this.cancelTarget) return;
      this.cancelLoading = true;
      try {
        const { data } = await Customers.cancelCustomerRedemption(
          this.userAccountId,
          this.cancelTarget.coupon_claim_id
        );
        this.$store.commit(
          'setNotification',
          {
            show: true,
            type: 'success',
            text: data.message,
          },
          { root: true }
        );
        this.closeCancelDialog();
        await this.loadData();
      } catch (ex) {
        this.$store.commit(
          'setNotification',
          {
            show: true,
            type: 'error',
            text: ex.response?.data?.message,
          },
          { root: true }
        );
      } finally {
        this.cancelLoading = false;
      }
    },
  },

  mounted() {
    this.loadData();
  },

  watch: {
    page(page) {
      this.loadData();
    },
    userAccountId(val) {
      if (val) {
        this.page = 1;
        this.allGifts = [];
        this.loadData();
      }
    },
    search(val) {
      this.debouncedSearch();
    },
  },

  created() {
    this.debouncedSearch = debounce(this.handleSearch, 500);
  },
};
</script>